// Open tabs: list operations shared by the browser-stored list (anonymous users) and the
// optimistic copy of the server list (signed-in users, see useOpenTabs.ts). Lists are ordered
// oldest first, one tab per video.

import { MAX_OPEN_TABS, type OpenTab, type SummaryLang } from '../../shared/api';
import { isSummaryLang } from '../../shared/lang';

/** What is known about a tab; missing or empty fields keep the existing values. */
export interface TabUpdate {
  videoId: string;
  lang?: SummaryLang | undefined;
  title?: string | undefined;
  channel?: string | undefined;
}

/** A change of the list; signed-in users send them to /api/tabs one by one. */
export type TabOp =
  /** Insert, or update lang/title/channel. `tab` holds the merged values at the time of the change. */
  | { kind: 'put'; tab: OpenTab }
  | { kind: 'delete'; videoId: string }
  /** Re-add tabs with their original `openedAt` (undo, sign-in); existing tabs win. */
  | { kind: 'import'; tabs: OpenTab[] };

function mergeTab(tab: OpenTab, update: TabUpdate): OpenTab {
  const lang = update.lang ?? tab.lang;
  return {
    videoId: tab.videoId,
    ...(lang ? { lang } : {}),
    title: update.title || tab.title,
    channel: update.channel || tab.channel,
    openedAt: tab.openedAt,
  };
}

function sameTab(a: OpenTab, b: OpenTab): boolean {
  return a.lang === b.lang && a.title === b.title && a.channel === b.channel && a.openedAt === b.openedAt;
}

/** Inserts a new tab at the end (dropping the oldest beyond MAX_OPEN_TABS) or updates an existing one. */
export function upsertTab(list: readonly OpenTab[], update: TabUpdate, now: Date = new Date()): OpenTab[] {
  const index = list.findIndex((tab) => tab.videoId === update.videoId);
  if (index >= 0) {
    const merged = mergeTab(list[index]!, update);
    return sameTab(merged, list[index]!) ? (list as OpenTab[]) : list.map((tab, i) => (i === index ? merged : tab));
  }
  const tab = mergeTab({ videoId: update.videoId, title: '', channel: '', openedAt: now.toISOString() }, update);
  return [...list, tab].slice(-MAX_OPEN_TABS);
}

/** The tab as it would be after `update` (for sending the full values to the server). */
export function mergedTab(list: readonly OpenTab[], update: TabUpdate, now: Date = new Date()): OpenTab {
  return upsertTab(list, update, now).find((tab) => tab.videoId === update.videoId)!;
}

export function removeTab(list: readonly OpenTab[], videoId: string): OpenTab[] {
  return list.some((tab) => tab.videoId === videoId) ? list.filter((tab) => tab.videoId !== videoId) : (list as OpenTab[]);
}

/** Adds the tabs that are missing (existing ones win), sorted by `openedAt`, capped like the server. */
export function importTabs(list: readonly OpenTab[], incoming: readonly OpenTab[]): OpenTab[] {
  const known = new Set(list.map((tab) => tab.videoId));
  const added = incoming.filter((tab) => !known.has(tab.videoId) && (known.add(tab.videoId), true));
  if (added.length === 0) return list as OpenTab[];
  return [...list, ...added].sort((a, b) => a.openedAt.localeCompare(b.openedAt)).slice(-MAX_OPEN_TABS);
}

export function applyTabOp(list: readonly OpenTab[], op: TabOp): OpenTab[] {
  switch (op.kind) {
    case 'put':
      // Keeps the position (and openedAt) of an existing tab, like the server.
      return upsertTab(list, op.tab, new Date(op.tab.openedAt));
    case 'delete':
      return removeTab(list, op.videoId);
    case 'import':
      return importTabs(list, op.tabs);
  }
}

export function applyTabOps(list: readonly OpenTab[], ops: readonly TabOp[]): OpenTab[] {
  return ops.reduce<OpenTab[]>((current, op) => applyTabOp(current, op), list as OpenTab[]);
}

/**
 * Appends `op` to a queue of unsent changes. Earlier unsent `put`s of the same video are dropped:
 * the new change carries (or supersedes) their values. `keepFirst` protects an op already in flight.
 */
export function enqueueTabOp(queue: readonly TabOp[], op: TabOp, keepFirst = false): TabOp[] {
  if (op.kind === 'import') return [...queue, op];
  const videoId = op.kind === 'put' ? op.tab.videoId : op.videoId;
  return [
    ...queue.filter((queued, index) => (keepFirst && index === 0) || !(queued.kind === 'put' && queued.tab.videoId === videoId)),
    op,
  ];
}

/** Where to go after closing `videoId`: the next tab, else the previous one, else none. */
export function neighborTab(list: readonly OpenTab[], videoId: string): OpenTab | null {
  const index = list.findIndex((tab) => tab.videoId === videoId);
  if (index === -1) return list[0] ?? null;
  return list[index + 1] ?? list[index - 1] ?? null;
}

// ---------------------------------------------------------------------------
// Validation of stored / received data

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function parseTab(value: unknown): OpenTab | null {
  if (!isObject(value) || typeof value.videoId !== 'string' || !value.videoId) return null;
  return {
    videoId: value.videoId,
    ...(isSummaryLang(value.lang) ? { lang: value.lang } : {}),
    title: typeof value.title === 'string' ? value.title : '',
    channel: typeof value.channel === 'string' ? value.channel : '',
    openedAt: typeof value.openedAt === 'string' ? value.openedAt : new Date(0).toISOString(),
  };
}

export function parseTabs(value: unknown): OpenTab[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const tabs: OpenTab[] = [];
  for (const item of value) {
    const tab = parseTab(item);
    if (tab && !seen.has(tab.videoId)) {
      seen.add(tab.videoId);
      tabs.push(tab);
    }
  }
  return tabs.slice(-MAX_OPEN_TABS);
}

export function parseTabOps(value: unknown): TabOp[] {
  if (!Array.isArray(value)) return [];
  const ops: TabOp[] = [];
  for (const item of value) {
    if (!isObject(item)) continue;
    if (item.kind === 'put') {
      const tab = parseTab(item.tab);
      if (tab) ops.push({ kind: 'put', tab });
    } else if (item.kind === 'delete' && typeof item.videoId === 'string') {
      ops.push({ kind: 'delete', videoId: item.videoId });
    } else if (item.kind === 'import') {
      ops.push({ kind: 'import', tabs: parseTabs(item.tabs) });
    }
  }
  return ops;
}
